import { useEffect, useMemo, useState } from "react";
import { findRegimen, regimenBadge } from "@/data/meal-plan";
import type { DayOfWeek, DayPlan, WeekPlan } from "@/types/meal-plan-state";
import { DAYS_OF_WEEK } from "@/types/meal-plan-state";
import { fetchWeekSnapshot, type WeekSummary } from "@/lib/api";
import { ownerScope } from "@/lib/week-owner";
import { useWeekPlan } from "@/context/week-plan-context";
import { migrateWeekPlan } from "@/lib/migrate-plan";
import { getMealChoices, getMealMenu } from "@/lib/random-plan";
import { formatWeekRange } from "@/lib/week-utils";
import { WeekCalendarPicker } from "./week-calendar-picker";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { hasEatenMarks, withEatenLossWarning } from "@/lib/owner-eaten";

interface DuplicateDayDialogProps {
  targetDay: DayOfWeek;
  targetRegimenId: string;
  currentWeekId: string;
  currentWeekPlan: WeekPlan;
  weekList: WeekSummary[];
  onDuplicate: (sourcePlan: DayPlan, mealIndices: number[]) => void;
  onClose: () => void;
  /**
   * Optional — when provided, the dialog only offers to copy these meal
   * indices (other meals are hidden) and pre-selects all of them. Used by
   * the Comidas screen so "Duplicar almuerzo" / "Duplicar cena" are
   * scoped to a single meal.
   */
  restrictMealIndices?: number[];
  /** Optional override of the dialog title (defaults to "Duplicar a {day}"). */
  title?: string;
}

/** Check if a source day has any actual selections */
function hasSelections(plan: DayPlan): boolean {
  if (!plan.meals) return false;
  return plan.meals.some((meal) =>
    meal.some((cg) => cg.selections.length > 0 || cg.skipped),
  );
}

export function DuplicateDayDialog({
  targetDay,
  targetRegimenId,
  currentWeekId,
  currentWeekPlan,
  weekList,
  onDuplicate,
  onClose,
  restrictMealIndices,
  title,
}: DuplicateDayDialogProps) {
  // Sources are the week owner's own weeks (a friend's in friend mode).
  const { owner } = useWeekPlan();
  const scope = useMemo(() => ownerScope(owner), [owner]);
  const [sourceWeekId, setSourceWeekId] = useState<string | null>(null);
  const [sourceDay, setSourceDay] = useState<DayOfWeek | null>(null);
  const [otherWeekPlan, setOtherWeekPlan] = useState<WeekPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMeals, setSelectedMeals] = useState<Set<number>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);

  const isCurrentWeek = sourceWeekId === currentWeekId;
  const weekPlan = isCurrentWeek ? currentWeekPlan : otherWeekPlan;

  // Fetch other week's plan when sourceWeekId changes
  useEffect(() => {
    if (!sourceWeekId || isCurrentWeek) {
      setOtherWeekPlan(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSourceDay(null);
    setSelectedMeals(new Set());
    fetchWeekSnapshot(scope, sourceWeekId)
      .then((snapshot) => snapshot.plan)
      .then((plan) => {
        if (!cancelled && plan) {
          const { plan: migrated } = migrateWeekPlan(plan);
          setOtherWeekPlan(migrated);
          setLoading(false);
        } else if (!cancelled) {
          setOtherWeekPlan(null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOtherWeekPlan(null);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [sourceWeekId, isCurrentWeek, scope]);

  // When source day changes, select all meals by default
  useEffect(() => {
    if (!sourceDay || !weekPlan) {
      setSelectedMeals(new Set());
      return;
    }
    const dayPlan = weekPlan[sourceDay];
    if (!dayPlan?.regimenId) return;
    const regimen = findRegimen(dayPlan.regimenId);
    if (!regimen) return;

    // Select all meals that have content (scoped to the restriction, if any)
    const indices = new Set<number>();
    regimen.meals.forEach((_, mi) => {
      if (restrictMealIndices && !restrictMealIndices.includes(mi)) return;
      const mealSels = dayPlan.meals?.[mi];
      if (mealSels && mealSels.some((cg) => cg.selections.length > 0 || cg.skipped)) {
        indices.add(mi);
      }
    });
    setSelectedMeals(indices);
  }, [sourceDay, weekPlan, restrictMealIndices]);

  // When week changes, reset day selection
  useEffect(() => {
    if (isCurrentWeek) {
      // Reset day selection when switching back to current week
      setSourceDay(null);
      setSelectedMeals(new Set());
    }
  }, [sourceWeekId]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const targetDayLabel = DAYS_OF_WEEK.find((d) => d.id === targetDay)!.label;
  const selectedPlan = sourceDay && weekPlan ? weekPlan[sourceDay] : null;
  const selectedRegimen = findRegimen(selectedPlan?.regimenId);

  function toggleMeal(mealIndex: number) {
    setSelectedMeals((prev) => {
      const next = new Set(prev);
      if (next.has(mealIndex)) {
        next.delete(mealIndex);
      } else {
        next.add(mealIndex);
      }
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-background rounded-t-2xl sm:rounded-2xl w-full max-w-md mx-auto p-5 space-y-4 safe-area-bottom max-h-[85dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="font-semibold text-base">{title ?? `Duplicar a ${targetDayLabel}`}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Elige la semana y el día del que quieres copiar
          </p>
        </div>

        {/* Week calendar picker */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Semana origen</label>
          <WeekCalendarPicker
            weekList={weekList}
            selectedWeekId={sourceWeekId}
            targetWeekId={currentWeekId}
            onSelectWeek={(weekId) => {
              setSourceWeekId(weekId);
              setSourceDay(null);
              setSelectedMeals(new Set());
            }}
          />
          {sourceWeekId && (
            <p className="text-xs text-primary font-medium mt-1">
              {formatWeekRange(sourceWeekId)}
              {isCurrentWeek && " (actual)"}
            </p>
          )}
        </div>

        {/* Day grid - only visible after picking a week */}
        {sourceWeekId && !loading && weekPlan && (
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Día origen</label>
            <div className="grid grid-cols-7 gap-1">
              {DAYS_OF_WEEK.map(({ id, label }) => {
                const dayPlan = weekPlan[id];
                const sameRegimen = dayPlan?.regimenId === targetRegimenId;
                const isSelf = isCurrentWeek && id === targetDay;
                const disabled = !sameRegimen || isSelf || !dayPlan?.regimenId;
                const noSelections = dayPlan ? !hasSelections(dayPlan) : true;
                const isSelected = sourceDay === id;

                return (
                  <button
                    key={id}
                    onClick={() => !disabled && !noSelections && setSourceDay(id)}
                    disabled={disabled || noSelections}
                    className={`flex flex-col items-center rounded-lg py-2 text-xs font-medium transition-colors ${
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : disabled || noSelections
                          ? "bg-muted/50 text-muted-foreground/40 cursor-not-allowed"
                          : "bg-muted text-foreground hover:bg-muted/80 cursor-pointer"
                    }`}
                    title={
                      isSelf
                        ? "Día actual"
                        : !sameRegimen
                          ? `Régimen diferente (${dayPlan?.regimenId ?? "ninguno"})`
                          : noSelections
                            ? "Sin selecciones"
                            : undefined
                    }
                  >
                    <span>{label}</span>
                    {dayPlan?.regimenId && (
                      <span className="text-[10px] opacity-60 mt-0.5">
                        {regimenBadge(dayPlan.regimenId)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {sourceWeekId && loading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {sourceWeekId && !loading && !weekPlan && !isCurrentWeek && (
          <p className="text-sm text-muted-foreground">No se encontró la semana</p>
        )}

        {/* Meal selection checkboxes */}
        {selectedPlan && selectedRegimen && (() => {
          const visibleIndices = selectedRegimen.meals
            .map((_, i) => i)
            .filter((i) => !restrictMealIndices || restrictMealIndices.includes(i));
          const allSelected =
            visibleIndices.length > 0 &&
            visibleIndices.every((i) => selectedMeals.has(i));
          return (
          <div className="border rounded-lg p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Comidas a copiar
              </h4>
              {visibleIndices.length > 1 && (
                <button
                  onClick={() => {
                    if (allSelected) {
                      setSelectedMeals(new Set());
                    } else {
                      setSelectedMeals(new Set(visibleIndices));
                    }
                  }}
                  className="text-[10px] text-primary hover:underline"
                >
                  {allSelected ? "Deseleccionar todo" : "Seleccionar todo"}
                </button>
              )}
            </div>
            {visibleIndices.map((mi) => {
              const meal = selectedRegimen.meals[mi]!;
              const mealSels = selectedPlan.meals?.[mi];
              if (!mealSels) return null;

              const menuId = selectedPlan.mealMenus?.[mi];
              const choices = getMealChoices(meal, menuId);
              const menu = getMealMenu(meal, menuId);
              const items: string[] = menu ? [menu.name] : [];
              for (let ci = 0; ci < choices.length; ci++) {
                const group = choices[ci]!;
                const cgSels = mealSels[ci];
                if (!cgSels || cgSels.skipped) continue;
                for (const sel of cgSels.selections) {
                  const opt = group.options.find((o) => o.id === sel.optionId);
                  if (!opt) continue;
                  items.push(sel.count > 1 ? `${opt.name} x${sel.count}` : opt.name);
                }
              }

              const note = selectedPlan.mealNotes?.[mi];
              const hasContent = items.length > 0 || mealSels.some((cg) => cg.skipped);
              const isChecked = selectedMeals.has(mi);

              return (
                <label
                  key={mi}
                  className={`flex items-start gap-2 rounded-md p-2 cursor-pointer transition-colors ${
                    isChecked ? "bg-primary/5" : "hover:bg-muted/50"
                  } ${!hasContent ? "opacity-40" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleMeal(mi)}
                    disabled={!hasContent}
                    className="mt-0.5 accent-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium">
                      {meal.label}
                      {note && <span className="font-normal text-muted-foreground italic ml-1">({note})</span>}
                    </p>
                    {items.length > 0 && (
                      <p className="text-xs text-muted-foreground truncate">{items.join(", ")}</p>
                    )}
                    {items.length === 0 && hasContent && (
                      <p className="text-xs text-muted-foreground italic">Saltada</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          );
        })()}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={() => {
              if (selectedPlan && selectedMeals.size > 0) {
                setShowConfirm(true);
              }
            }}
            disabled={!selectedPlan || selectedMeals.size === 0}
            className="flex-1 text-sm font-medium py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Duplicar {selectedMeals.size > 0 ? `(${selectedMeals.size})` : ""}
          </button>
          <button
            onClick={onClose}
            className="text-sm font-medium py-2 px-4 rounded-md border hover:bg-muted transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title="¿Reemplazar las comidas seleccionadas?"
        description={withEatenLossWarning(
          "Las comidas seleccionadas se reemplazarán con las del día origen.",
          owner,
          hasEatenMarks(currentWeekPlan[targetDay].meals, selectedMeals),
        )}
        confirmText="Reemplazar"
        destructive
        onConfirm={() => {
          if (selectedPlan && selectedMeals.size > 0) {
            onDuplicate(selectedPlan, Array.from(selectedMeals));
          }
        }}
      />
    </div>
  );
}
